import React, {
	useEffect,
	useState,
	useMemo,
	useCallback,
	useRef,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
	getEstablishmentTypeCodes,
	getMenuScopesRole,
	getRole,
} from '../slices/thunks';
import { createSelector } from 'reselect';
import { useDispatch, useSelector } from 'react-redux';
import useEstablishmentAssignationUser from '../Components/Hooks/useEstablishmentAssignationUser';
import {
	setCurrentItemsMenu,
	setRequireInitRedirect,
} from '../slices/settings/reducer';
import { id } from 'date-fns/locale';

// Cache para menu computation
const menuCache = {
	lastComputedKey: null,
	lastResult: null,
};

const Navdata = () => {
	const history = useNavigate();
	const dispatch = useDispatch();

	const [currentMenuItems, setCurrentMenuItems] = useState([]);
	const [currentCodes, setCurrentCodes] = useState([]);
	const [establishmentActiveId, setEstablishmentActiveId] = useState('');

	const role = JSON.parse(localStorage.getItem('authUser'))
		? JSON.parse(localStorage.getItem('authUser')).role_id
		: null;

	const selectorProfile = createSelector(
		state => state.Profile,
		profile => ({
			user: profile.user,
			establishmentActive: profile.establishmentActive,
			establishmentActiveLoaded: profile.establishmentActiveLoaded,
		}),
	);
	const { user, establishmentActive, establishmentActiveLoaded } =
		useSelector(selectorProfile);

	const selectorPowerGenerationType = createSelector(
		state => state.EstablishmentTypes,
		data => ({
			filter_codes: data.itemEstablishmentTypeDetail,
		}),
	);
	const { filter_codes } = useSelector(selectorPowerGenerationType);

	useEffect(() => {
		if (establishmentActive != null) {
			setEstablishmentActiveId(establishmentActive.item._id);
		}
	}, [establishmentActive]);

	const [isCompany, setIsCompany] = useState(false);

	const selectRole = createSelector(
		state => state.Roles,
		data => ({
			menu_scopes: data.menuScopes,
		}),
	);
	const { menu_scopes } = useSelector(selectRole);

	useEffect(() => {
		if (role) {
			dispatch(getMenuScopesRole(role));
		}
	}, [role]);

	useEffect(() => {
		setCurrentCodes(filter_codes || []);
	}, [filter_codes]);

	useEffect(() => {
		if (user && user.establishment_type_ids) {
			dispatch(
				getEstablishmentTypeCodes({
					list_ids: Array.isArray(user.establishment_type_ids)
						? user.establishment_type_ids
						: [],
				}),
			);
		}
	}, [user]);

	useEffect(() => {
		dispatch(setCurrentItemsMenu(JSON.stringify(currentMenuItems)));
	}, [currentMenuItems]);

	useEffect(() => {
		if (menu_scopes?.length && establishmentActiveLoaded) {
			setCurrentMenuItems(
				handlePushItemsMenuByScopes(layoutMenuData, currentCodes, true),
			);
		}
	}, [establishmentActiveId, currentCodes]);

	useEffect(() => {
		if (menu_scopes?.length && establishmentActiveLoaded) {
			setCurrentMenuItems(
				handlePushItemsMenuByScopes(layoutMenuData, currentCodes, true),
			);
		}
	}, [menu_scopes, establishmentActiveLoaded, currentCodes]);

	// Memoized function to handle menu filtering with caching
	const handlePushItemsMenuByScopes = useCallback(
		(menuItems, currentCodes, isMainCall = false) => {
			// Solo usar caché para la llamada principal, no para las recursivas
			if (isMainCall) {
				// Create a unique key for caching based on input parameters
				const cacheKey = JSON.stringify({
					menuScopesLength: menu_scopes?.length || 0,
					currentCodesLength: currentCodes?.length || 0,
					establishmentActiveId,
				});

				// Return cached result if available and inputs haven't changed
				if (
					menuCache.lastComputedKey === cacheKey &&
					menuCache.lastResult
				) {
					return menuCache.lastResult;
				}
			}

			const result = menuItems
				.map(menuItem => {
					// Si el menú tiene filterEstablishmentTypeCode, verificamos si está en currentCodes
					if (
						(!menuItem.filterEstablishmentTypeCode ||
							menuItem.filterEstablishmentTypeCode.some(code =>
								currentCodes.includes(code),
							)) &&
						(menu_scopes.includes(menuItem.id) ||
							menuItem.id === undefined)
					) {
						let newItems = [];
						if (menuItem?.subItems?.length) {
							newItems = menuItem.subItems;
							return {
								...menuItem,
								subItems: handlePushItemsMenuByScopes(
									newItems,
									currentCodes,
									false, // Llamada recursiva, no usar caché
								),
							};
						} else if (menuItem?.childItems?.length) {
							newItems = menuItem.childItems;
							return {
								...menuItem,
								childItems: handlePushItemsMenuByScopes(
									newItems,
									currentCodes,
									false, // Llamada recursiva, no usar caché
								),
							};
						} else {
							return menuItem;
						}
					}
				})
				.filter(menuItem => menuItem !== undefined);

			// Solo cachear el resultado de la llamada principal
			if (isMainCall) {
				const cacheKey = JSON.stringify({
					menuScopesLength: menu_scopes?.length || 0,
					currentCodesLength: currentCodes?.length || 0,
					establishmentActiveId,
				});
				menuCache.lastComputedKey = cacheKey;
				menuCache.lastResult = result;
			}

			return result;
		},
		[menu_scopes, establishmentActiveId],
	);

	//state data
	const [isDashboard, setIsDashboard] = useState(false);
	const [isApps, setIsApps] = useState(false);
	const [isAuth, setIsAuth] = useState(false);
	const [isPages, setIsPages] = useState(false);
	const [isBaseUi, setIsBaseUi] = useState(false);
	const [isAdvanceUi, setIsAdvanceUi] = useState(false);
	const [isForms, setIsForms] = useState(false);
	const [isTables, setIsTables] = useState(false);
	const [isCharts, setIsCharts] = useState(false);
	const [isIcons, setIsIcons] = useState(false);
	const [isMaps, setIsMaps] = useState(false);
	const [isMultiLevel, setIsMultiLevel] = useState(false);

	// Apps
	const [isEmail, setEmail] = useState(false);
	const [isSubEmail, setSubEmail] = useState(false);
	const [isEcommerce, setIsEcommerce] = useState(false);
	const [isProjects, setIsProjects] = useState(false);
	const [isTasks, setIsTasks] = useState(false);
	const [isCRM, setIsCRM] = useState(false);
	const [isCrypto, setIsCrypto] = useState(false);
	const [isInvoices, setIsInvoices] = useState(false);
	const [isSupportTickets, setIsSupportTickets] = useState(false);
	const [isNFTMarketplace, setIsNFTMarketplace] = useState(false);
	const [isLanding, setIsLanding] = useState(false);
	const [isJobs, setIsJobs] = useState(false);
	const [isJobList, setIsJobList] = useState(false);
	const [isCandidateList, setIsCandidateList] = useState(false);

	// Authentication
	const [isSignIn, setIsSignIn] = useState(false);
	const [isSignUp, setIsSignUp] = useState(false);
	const [isPasswordReset, setIsPasswordReset] = useState(false);
	const [isPasswordCreate, setIsPasswordCreate] = useState(false);
	const [isLockScreen, setIsLockScreen] = useState(false);
	const [isLogout, setIsLogout] = useState(false);
	const [isSuccessMessage, setIsSuccessMessage] = useState(false);
	const [isVerification, setIsVerification] = useState(false);
	const [isError, setIsError] = useState(false);

	// Pages
	const [isProfile, setIsProfile] = useState(false);

	// Admin
	const [isAdmin, setIsAdmin] = useState(false);

	// Charts
	const [isApex, setIsApex] = useState(false);
	const [isResidueGuidesFlatMenu, setIsResidueGuidesFlatMenu] =
		useState(false);
	const [isRespelMenu, setIsRespelMenu] = useState(false);

	// Multi Level
	const [isLevel1, setIsLevel1] = useState(false);
	const [isLevel2, setIsLevel2] = useState(false);

	const [iscurrentState, setIscurrentState] = useState('Dashboard');

	function updateIconSidebar(e) {
		if (e && e.target && e.target.getAttribute('subitems')) {
			const ul = document.getElementById('two-column-menu');
			const iconItems = ul.querySelectorAll('.nav-icon.active');
			let activeIconItems = [...iconItems];
			activeIconItems.forEach(item => {
				item.classList.remove('active');
				var id = item.getAttribute('subitems');
				if (document.getElementById(id))
					document.getElementById(id).classList.remove('show');
			});
		}
	}

	// Establishment
	const [isEstablishment, setIsEstablishment] = useState(false);

	useEffect(() => {
		document.body.classList.remove('twocolumn-panel');

		if (iscurrentState !== 'Empresa') {
			setIsCompany(false);
		}

		if (iscurrentState !== 'Dashboard') {
			setIsDashboard(false);
		}
		if (iscurrentState !== 'Apps') {
			setIsApps(false);
		}
		if (iscurrentState !== 'Auth') {
			setIsAuth(false);
		}
		if (iscurrentState !== 'Pages') {
			setIsPages(false);
		}
		if (iscurrentState !== 'Landing') {
			setIsLanding(false);
		}
		if (iscurrentState !== 'BaseUi') {
			setIsBaseUi(false);
		}
		if (iscurrentState !== 'AdvanceUi') {
			setIsAdvanceUi(false);
		}
		if (iscurrentState !== 'Forms') {
			setIsForms(false);
		}
		if (iscurrentState !== 'Tables') {
			setIsTables(false);
		}
		if (iscurrentState !== 'Charts') {
			setIsCharts(false);
		}
		if (iscurrentState !== 'Icons') {
			setIsIcons(false);
		}
		if (iscurrentState !== 'Maps') {
			setIsMaps(false);
		}
		if (iscurrentState !== 'MuliLevel') {
			setIsMultiLevel(false);
		}
		if (iscurrentState !== 'Admin') {
			setIsAdmin(false);
		}
		if (iscurrentState === 'Widgets') {
			history('/widgets');
			document.body.classList.add('twocolumn-panel');
		}
		if (isEstablishment !== 'Establishment') {
			setIsEstablishment(false);
		}
	}, [
		history,

		isCompany,

		isDashboard,
		isApps,
		isAuth,
		isPages,
		isBaseUi,
		isAdvanceUi,
		isForms,
		isTables,
		isCharts,
		isIcons,
		isMaps,
		isMultiLevel,
		isAdmin,
	]);

	const layoutMenuData = [
		{
			label: 'Menu',
			isHeader: true,
		},
		{
			id: 'home',
			label: 'Inicio',
			icon: Icons.house,
			link: '/#',
			subItems: [
				{
					id: 'calendar',
					icon: Icons.calendar,
					label: 'Calendario',
					link: '/calendar',
					parentId: 'home',
				},
			],
		},
		{
			id: 'company',
			label: 'Empresa',
			icon: Icons.company,
			link: '/#',
			click: function (e) {
				e.preventDefault();
				setIsCharts(!isCompany);
				setIscurrentState('Empresa');
				updateIconSidebar(e);
			},
			stateVariables: isCompany,
			subItems: [
				// { id: 'establishment-dashboard', label: 'Panel', link: '/establishment/dashboard', icon: Icons.dashboard, parentId: 'company', },
				{
					id: 'establishments',
					label: 'Establecimientos',
					icon: Icons.establishments,
					link: '/establishments',
					parentId: 'company',
				},
				{
					id: 'persons',
					label: 'Personas',
					icon: Icons.persons,
					link: '/persons',
					parentId: 'company',
				},
				{
					id: 'warehouses',
					label: 'Bodegas',
					icon: Icons.warehouse,
					link: '/warehouses',
					parentId: 'company',
				},
				{
					id: 'establishment-config',
					label: 'Configuración',
					link: '/#',
					icon: Icons.settings,
					isChildItem: true,
					click: function (e) {
						e.preventDefault();
						setIsApex(!isApex);
					},
					stateVariables: isApex,
					childItems: [
						{
							id: 'holdings',
							label: 'Holdings',
							link: '/establishment/holdings',
							icon: Icons.holdings,
							parentId: 'establishment',
						},
						{
							id: 'companies',
							label: 'Empresas',
							link: '/companies',
							icon: Icons.companies,
							parentId: 'establishment',
						},
						{
							id: 'departments',
							label: 'Departamentos',
							link: '/company/departments',
							icon: Icons.departments,
							parentId: 'establishment',
						},
						{
							id: 'cost_center',
							label: 'Centro de Costo',
							link: '/company/cost-center',
							icon: Icons.costCenter,
							parentId: 'establishment',
						},
					],
				},
			],
		},
		{
			id: 'establishment',
			label: 'Centro',
			icon: Icons.establishments,
			link: '/#',
			click: function (e) {
				e.preventDefault();
				setIsCharts(!isCompany);
				setIscurrentState('Bodega');
				updateIconSidebar(e);
			},
			stateVariables: isCompany,
			subItems: [
				// {
				// 	id: 'establishment-dashboard',
				// 	label: 'Panel',
				// 	link: '/establishment/summary/' + establishmentActiveId,
				// 	icon: 'ri-dashboard-line',
				// 	parentId: 'establishment',
				// },
				{
					id: 'establishment-summary',
					label: 'Expediente',
					link: '/establishment/record/' + establishmentActiveId,
					icon: Icons.summary,
					parentId: 'establishment',
				},
				{
					id: 'establishment-activity',
					label: 'Actividades',
					link: '/establishment/activities/' + establishmentActiveId,
					icon: Icons.handIcons,
					parentId: 'establishment',
				},
			],
		},
		{
			id: 'warehouse',
			label: 'Bodega',
			icon: Icons.warehouse,
			link: '/#',
			click: function (e) {
				e.preventDefault();
				setIsCharts(!isCompany);
				setIscurrentState('Bodega');
				updateIconSidebar(e);
			},
			stateVariables: isCompany,
			subItems: [
				{
					id: 'warehouse-guide-register',
					label: 'Generar Guía de Despacho',
					link: '/warehouse/guide/register',
					icon: Icons.guide,
					parentId: 'warehouse',
				},
				{
					id: 'warehouse-guides-list',
					label: 'Guías de Despacho',
					link: '/warehouse/guides',
					icon: 'ri-file-list-line',
					parentId: 'warehouse',
				},
				{
					id: 'warehouse-dashboard',
					label: 'Panel',
					link: '/warehouse/dashboard',
					icon: Icons.dashboard,
					parentId: 'warehouse',
				},
				{
					id: 'warehouse-list',
					label: 'Lista de items',
					link: '/warehouse/items/' + establishmentActiveId,
					icon: Icons.list,
					parentId: 'warehouse',
				},
				{
					id: 'warehouse-ppe',
					label: 'Bodega de EPP',
					link: '/warehouse/ppe',
					icon: Icons.warehouse,
					isChildItem: true,
					click: function (e) {
						e.preventDefault();
						setIsApex(!isApex);
					},
					stateVariables: isApex,
					childItems: [
						{
							id: 'warehouse-ppe-items',
							label: 'Lista de Items',
							link: establishmentActiveId
								? `/warehouse/${establishmentActiveId}/ppe/items`
								: '/warehouse/ppe',
							icon: 'ri-dashboard-line',
							parentId: 'warehouse-ppe',
						},
						{
							id: 'warehouse-ppe-transfers',
							label: 'Transferencias',
							link: establishmentActiveId
								? `/warehouse/${establishmentActiveId}/ppe/transfers`
								: '/warehouse/ppe',
							icon: 'ri-arrow-up-down-fill',
							parentId: 'warehouse-ppe',
						},
					],
				},
				{
					id: 'warehouse-reception',
					label: 'Recepcionar Guía de Despacho',
					link: '/residue/warehouse/reception',
					icon: Icons.chevronLeft,
					parentId: 'warehouse',
				},
				{
					id: 'warehouse-config',
					label: 'Configuración',
					link: '/#',
					icon: Icons.settings,
					isChildItem: true,
					click: function (e) {
						e.preventDefault();
						setIsApex(!isApex);
					},
					stateVariables: isApex,
					childItems: [
						{
							id: 'warehouse-config-category',
							label: 'Categorías',
							link: '/warehouse/category',
							icon: Icons.list,
							parentId: 'config-warehouse',
						},
						{
							id: 'warehouse-config-group',
							label: 'Grupo',
							link: '/warehouse/group',
							icon: Icons.list,
							parentId: 'config-warehouse',
						},
					],
				},
			],
		},
		{
			id: 'normative',
			label: 'Normativa',
			icon: Icons.normative,
			link: '/#',
			click: function (e) {
				e.preventDefault();
				setIsEstablishment(!isEstablishment);
				setIscurrentState('Establishment');
				updateIconSidebar(e);
			},
			stateVariables: isEstablishment,
			subItems: [
				{
					id: 'fiscalization',
					label: 'Fiscalización',
					link: '/#',
					icon: 'icon-clipboard-check',
					isChildItem: true,
					click: function (e) {
						e.preventDefault();
						setIsApex(!isApex);
					},
					stateVariables: isApex,
					childItems: [
						{
							id: 'fiscalization-dashboard',
							label: 'Panel',
							link: '/fiscalizacion/dashboard',
							icon: Icons.dashboard,
							parentId: 'fiscalization',
						},
						{
							id: 'fiscalization-list',
							label: 'Fiscalizaciones',
							link: '/fiscalizacion/fiscalizaciones',
							icon: 'icon-layout-list',
							parentId: 'fiscalization',
						},
					],
				},
				// {
				// 	id: 'normative-list',
				// 	label: 'Normativas',
				// 	link: '/normative/list',
				// 	icon: Icons.guide,
				// 	parentId: 'normative',
				// },
				// {
				// 	id: 'obligation-list',
				// 	label: 'Obligaciones',
				// 	link: '/normative/obligations',
				// 	icon: Icons.landmark,
				// 	parentId: 'normative',
				// },
				// {
				// 	id: 'obligation-kanban',
				// 	label: 'Kanban',
				// 	link: '/normative/kanban',
				// 	icon: 'icon-circle-check-big',
				// 	parentId: 'normative',
				// },
				// {
				// 	id: 'water-rights',
				// 	label: 'Derechos de Agua',
				// 	link: '/#',
				// 	icon: Icons.water,
				// 	isChildItem: true,
				// 	click: function (e) {
				// 		e.preventDefault();
				// 		setIsApex(!isApex);
				// 	},
				// 	stateVariables: isApex,
				// 	childItems: [
				// 		{
				// 			id: 'water-rights-dashboard',
				// 			label: 'Panel',
				// 			link: '/water-rights/dashboard',
				// 			icon: Icons.dashboard,
				// 			parentId: 'water-rights',
				// 		},
				// 		{
				// 			id: 'water-rights-list',
				// 			label: 'Lista',
				// 			link: '/water-rights/list',
				// 			icon: Icons.list,
				// 			parentId: 'water-rights',
				// 		},
				// 	],
				// },
			],
		},
		{
			id: 'finance',
			label: 'Finanza',
			icon: Icons.landmark,
			link: '/#',
			click: function (e) {
				e.preventDefault();
				setIsEstablishment(!isEstablishment);
				setIscurrentState('Finance');
				updateIconSidebar(e);
			},
			stateVariables: isEstablishment,
			subItems: [
				{
					id: 'finance-data',
					label: 'Datos',
					link: '/finance/data',
					icon: Icons.database,
					parentId: 'finance',
				},
				{
					id: 'finance-monthly',
					label: 'Reportes',
					link: '/finance/reports',
					icon: Icons.reports,
					parentId: 'finance',
				},
				{
					id: 'finance-build',
					label: 'Constructor',
					link: '/#',
					icon: Icons.monitor,
					isChildItem: true,
					click: function (e) {
						e.preventDefault();
						setIsApex(!isApex);
					},
					stateVariables: isApex,
					childItems: [
						{
							id: 'finance-build-templates',
							label: 'Plantillas',
							link: '/finance/report-template',
							icon: Icons.reportTemplates,
							parentId: 'finance-build',
						},
						{
							id: 'finance-build-config',
							label: 'Configuración',
							link: '/#',
							icon: Icons.settings,
							isChildItem: true,
							click: function (e) {
								e.preventDefault();
								setIsApex(!isApex);
							},
							stateVariables: isApex,
							childItems: [
								{
									id: 'finance-build-config-screens',
									label: 'Pantallas',
									link: '/finance/pages',
									icon: Icons.screens,
									parentId: 'finance-build-config',
								},
								{
									id: 'finance-build-config-graphs',
									label: 'Gráficos',
									link: '/finance/graph',
									icon: Icons.graphs,
									parentId: 'finance-build-config',
								},
								{
									id: 'finance-build-config-screen-types',
									label: 'Tipos de Pantallas',
									link: '/finance/page-types',
									icon: Icons.screenTypes,
									parentId: 'finance-build-config',
								},
							],
						},
					],
				},
			],
		},
		{
			id: 'supply',
			label: 'Abastecimiento',
			icon: Icons.supply,
			link: '/#',
			click: function (e) {
				e.preventDefault();
				updateIconSidebar(e);
			},
			stateVariables: isCompany,
			subItems: [
				{
					id: 'fuel-supply',
					label: 'Combustible',
					link: '/#',
					icon: Icons.fuel,
					isChildItem: true,
					click: function (e) {
						e.preventDefault();
						setIsApex(!isApex);
					},
					stateVariables: isApex,
					childItems: [
						{
							id: 'fuel-dashboard',
							label: 'Panel',
							link:
								'/establishment/summary/fuel/' + establishmentActiveId,
							icon: Icons.dashboard,
							parentId: 'fuel-supply',
						},
						{
							id: 'fuel-supply-panel',
							label: 'Panel',
							link: '/supply/fuel/dashboard',
							icon: Icons.dashboard,
							parentId: 'fuel-supply',
						},
						{
							id: 'fuel-management-dashboard',
							label: 'Panel Gerencia',
							link: '/establishment/management-summary/fuel',
							icon: 'ri-bar-chart-grouped-line',
							parentId: 'fuel-supply',
						},
						{
							id: 'fuel-supply-management',
							label: 'Control de Combustible',
							link: '/supply/fuel/summary',
							icon: Icons.gauge,
							parentId: 'fuel-supply',
						},
						{
							id: 'fuel-supply-movement',
							label: 'Movimiento de Combustible',
							link: '/supply/fuel-supply/' + establishmentActiveId,
							icon: Icons.fuel,
							parentId: 'fuel-supply',
						},
						{
							id: 'supply-config',
							label: 'Configuración',
							link: '/#',
							icon: Icons.settings,
							isChildItem: true,
							click: function (e) {
								e.preventDefault();
								setIsApex(!isApex);
							},
							stateVariables: isApex,
							childItems: [
								{
									id: 'fuel-type',
									label: 'Tipo Combustible',
									link: '/supply/fuel-type',
									icon: Icons.list,
									parentId: 'supply-config',
								},
							],
						},
					],
				},
				{
					id: 'ppe-supply',
					label: 'Gestión de EPP',
					link: '/#',
					icon: Icons.shirt,
					parentId: 'supply',
					isChildItem: true,
					click: function (e) {
						e.preventDefault();
						setIsApex(!isApex);
					},
					stateVariables: isApex,
					childItems: [
						{
							id: 'ppe-dashboard',
							label: 'Panel',
							link: '/supply/ppe/summary',
							icon: Icons.dashboard,
							parentId: 'ppe-supply',
						},
						{
							id: 'ppe-management-dashboard',
							label: 'Panel Gerencia',
							link: '/establishment/management-summary/ppe',
							icon: 'ri-bar-chart-grouped-line',
							parentId: 'fuel-supply',
						},
						{
							id: 'ppe-dashboard-center',
							label: 'Panel Centro',
							link:
								'/supply/ppe/summary/center/' + establishmentActiveId,
							icon: Icons.dashboard,
							parentId: 'ppe-supply',
						},
						{
							id: 'ppe-list',
							label: 'Asignaciones',
							link: '/supply/ppe/list',
							icon: Icons.list,
							parentId: 'ppe-supply',
						},
						{
							id: 'ppe-suppply-list',
							label: 'Control de EPP',
							link: '/supply/ppe',
							icon: Icons.gauge,
							parentId: 'ppe-supply',
						},
						{
							id: 'ppe-person-assignments-list',
							label: 'Control de EPP por Persona',
							link: '/supply/ppe/person-assignments',
							icon: Icons.gauge,
							parentId: 'ppe-supply',
						},
						{
							id: 'ppe-suppply-motion',
							label: 'Movimiento de EPP',
							link: '/supply/ppe/motion',
							icon: Icons.archive,
							parentId: 'ppe-supply',
						},
					],
				},
				{
					id: 'supply-config',
					label: 'Configuración',
					link: '/#',
					icon: Icons.settings,
					isChildItem: true,
					click: function (e) {
						e.preventDefault();
						setIsApex(!isApex);
					},
					stateVariables: isApex,
					parentId: 'supply',
					childItems: [
						{
							id: 'periods',
							label: 'Períodos',
							link: '/supply/periods',
							icon: Icons.calendarDays,
							parentId: 'supply-config',
						},
						{
							id: 'position-ppe',
							label: 'Cargos EPP',
							link: '/supply/positions',
							icon: 'ri-list-check-2',
							parentId: 'fuel-supply',
						},
					],
				},
			],
		},
		{
			id: 'production',
			label: 'Producción',
			icon: Icons.bubbles,
			link: '/#',
			click: function (e) {
				e.preventDefault();
				setIsCharts(!isCompany);
				setIscurrentState('Bodega');
				updateIconSidebar(e);
			},
			stateVariables: isCompany,
			subItems: [
				{
					id: 'production-summary',
					label: 'Ciclo Productivo',
					link: '/production/productive-cycle',
					icon: Icons.productiveCycle,
					parentId: 'production',
				},
				{
					id: 'production-config',
					label: 'Configuración',
					link: '/#',
					icon: Icons.settings,
					isChildItem: true,
					click: function (e) {
						e.preventDefault();
						setIsApex(!isApex);
					},
					stateVariables: isApex,
					childItems: [
						{
							id: 'productive-cycle-stage',
							label: 'Etapas del ciclo productivo',
							link: '/production/productive-cycle-stage',
							icon: Icons.list,
							parentId: 'production-config',
						},
					],
				},
			],
		},
		{
			id: 'operation',
			label: 'Operaciones',
			icon: Icons.tools,
			link: '/#',
			click: function (e) {
				e.preventDefault();
				setIsCharts(!isCompany);
				setIscurrentState('Bodega');
				updateIconSidebar(e);
			},
			stateVariables: isCompany,
			subItems: [
				{
					id: 'operation-maintenance',
					label: 'Mantención',
					link: '/#',
					icon: Icons.tools,
					isChildItem: true,
					click: function (e) {
						e.preventDefault();
						setIsApex(!isApex);
					},
					stateVariables: isApex,
					childItems: [
						{
							id: 'operation-maintenance-calendar',
							icon: Icons.calendar,
							label: 'Calendario de Mantenciones',
							link: '/operation/calendar/maintenance',
							parentId: 'operation-maintenance',
						},
						{
							id: 'operation-maintenance-activity',
							icon: Icons.handIcons,
							label: 'Actividades',
							link: '/operation/activity/maintenance',
							parentId: 'operation-maintenance',
						},
					],
				},
				{
					id: 'operation-feeding-barges',
					label: 'Artefacto Naval',
					link: '/operation/feeding-barges',
					icon: Icons.establishments,
					filterEstablishmentTypeCode: ['SWE', 'LWE'],
					parentId: 'operation',
				},
				{
					id: 'operation-fuel-tank',
					label: 'Estanques de Combustible',
					link: '/supply/fuel-tank',
					icon: Icons.database,
					parentId: 'operation',
				},
				{
					id: 'operation-consumption-source',
					label: 'Fuentes de Consumo',
					link: '/operation/consumption-source',
					icon: Icons.consumptionSource,
					parentId: 'operation',
				},
				{
					id: 'operation-power-generation-source',
					label: 'Fuentes de Generación de Energía',
					link: '/#',
					icon: Icons.power,
					isChildItem: true,
					click: function (e) {
						e.preventDefault();
						setIsApex(!isApex);
					},
					stateVariables: isApex,
					childItems: [
						{
							id: 'operation-power-generation-source-register',
							label: 'Ver Fuentes de Generación de Energía',
							link: '/operation/power-generation-source',
							icon: 'ri-list-check-2',
							parentId: 'operation-power-generation-source',
						},
						{
							id: 'operation-power-generation-source-update',
							label: 'Actualizar Catastro',
							link: '/environment/power-generation-source',
							icon: 'ri-refresh-line',
							parentId: 'operation-power-generation-source',
						},
					],
				},
				{
					id: 'operation-production-silo',
					label: 'Silo de Alimentos',
					link: '/operation/feeding-silo',
					icon: Icons.silo,
					filterEstablishmentTypeCode: ['SWE', 'LWE'],
					parentId: 'operation',
				},
				{
					id: 'operation-hour-meter',
					label: 'Registrar Horómetro/Consumo',
					link: '/operation/hour-meter/' + establishmentActiveId,
					icon: Icons.gauge,
					parentId: 'operation',
				},
				{
					id: 'operation-config',
					label: 'Configuración',
					link: '/#',
					icon: Icons.settings,
					isChildItem: true,
					click: function (e) {
						e.preventDefault();
						setIsApex(!isApex);
					},
					stateVariables: isApex,
					childItems: [
						{
							id: 'maintenance-company',
							label: 'Empresa de Mantenimiento',
							link: '/operation/maintenance-company',
							icon: Icons.maintenance,
							parentId: 'operation',
						},
					],
				},
			],
		},
		{
			id: 'environment',
			label: 'Medio Ambiente',
			icon: Icons.environment,
			link: '/#',
			click: function (e) {
				e.preventDefault();
				setIsCharts(!isCompany);
				setIscurrentState('Empresa');
				updateIconSidebar(e);
			},
			stateVariables: isCompany,
			subItems: [
				{
					id: 'residue',
					label: 'Residuos',
					link: '/#',
					icon: Icons.recycle,
					isChildItem: true,
					click: function (e) {
						e.preventDefault();
						setIsApex(!isApex);
					},
					stateVariables: isApex,
					childItems: [
						{
							id: 'residue-dashboard',
							label: 'Panel',
							link: '/residue/dashboard',
							icon: Icons.dashboard,
							parentId: 'residue',
						},
						{
							id: 'residue-respel',
							label: 'RESPEL',
							link: '/#',
							icon: Icons.alert,
							isChildItem: true,
							click: function (e) {
								e.preventDefault();
								setIsRespelMenu(!isRespelMenu);
							},
							stateVariables: isRespelMenu,
							childItems: [
								{
									id: 'residue-respel-movements',
									label: 'Movimientos RESPEL',
									link: establishmentActiveId
										? `/residue/respel/movements/${establishmentActiveId}`
										: '/residue/respel/movements',
									icon: Icons.new,
									parentId: 'residue-respel',
								},
								{
									id: 'residue-respel-stock',
									label: 'Stock RESPEL',
									link: establishmentActiveId
										? `/residue/respel/stock/${establishmentActiveId}`
										: '/residue/respel/stock',
									icon: Icons.warehouse,
									parentId: 'residue-respel',
								},
							],
						},
						{
							id: 'residue-guides-list',
							label: 'Ver Guías de Despacho',
							link: '/residue/guides',
							icon: Icons.list,
							parentId: 'residue',
						},
						{
							id: 'residue-guides-flat',
							label: 'Guías por ítems',
							link: '/#',
							icon: 'ri-grid-line',
							isChildItem: true,
							click: function (e) {
								e.preventDefault();
								setIsResidueGuidesFlatMenu(!isResidueGuidesFlatMenu);
							},
							stateVariables: isResidueGuidesFlatMenu,
							childItems: [
								/* Vista “Todas” (/residue/guides-flat sin filtro) retirada a pedido — dejar comentado por si se reactiva.
								{
									id: 'residue-guides-flat-all',
									label: 'Todas',
									link: '/residue/guides-flat',
									icon: 'ri-grid-line',
									parentId: 'residue-guides-flat',
								},
								*/
								{
									id: 'residue-guides-flat-peligrosos',
									label: 'Peligrosos',
									link: '/residue/guides-flat/peligrosos',
									icon: 'ri-grid-line',
									parentId: 'residue-guides-flat',
								},
								{
									id: 'residue-guides-flat-no-peligrosos',
									label: 'No peligrosos',
									link: '/residue/guides-flat/no-peligrosos',
									icon: 'ri-grid-line',
									parentId: 'residue-guides-flat',
								},
							],
						},
						// Legacy: /residue/guides/register → usar Bodega → Generar Guía (?category=residuos)
						{
							id: 'residue-warehouse-reception',
							label: 'Recepcionar Guía de Despacho',
							link: '/residue/warehouse/reception',
							icon: Icons.chevronLeft,
							parentId: 'residue',
						},
						{
							id: 'residue-final-destination-certification-process',
							label: 'PreValidar/Certificar Guía de Despacho',
							link: '/residue/final_destination/certification-process',
							icon: Icons.check2,
							parentId: 'residue',
						},
						// { id: 'receptionFinalDestination', label: 'Recepcionar Guías', link: '/residue/final_destination/reception', icon: Icons.chevronLeft, parentId: 'residue', },
						{
							id: 'residue-final-destination-reception',
							label: 'Recepcionar Guías',
							link: '/residue/final_destination/reception',
							icon: Icons.chevronLeft,
							parentId: 'residue',
						},
						{
							id: 'residue-statement',
							label: 'Declaración',
							link: '/#',
							icon: Icons.statement,
							isChildItem: true,
							click: function (e) {
								e.preventDefault();
								setIsApex(!isApex);
							},
							stateVariables: isApex,
							childItems: [
								{
									id: 'residue-statement-list',
									label: 'Ver Declaraciones',
									link: '/residue/statement',
									icon: Icons.list,
									parentId: 'residue-statement',
								},
								{
									id: 'residue-statement-new',
									label: 'Nueva Declaración',
									link: '/residue/statement/new',
									icon: Icons.new,
									parentId: 'residue-statement',
								},
							],
						},
						{
							id: 'residue-config',
							label: 'Configuración',
							link: '/#',
							icon: Icons.settings,
							isChildItem: true,
							click: function (e) {
								e.preventDefault();
								setIsApex(!isApex);
							},
							stateVariables: isApex,
							childItems: [
								{
									id: 'residue-config-final-destination-list',
									label: 'Destinos Finales',
									link: '/residue/final-destinations',
									icon: Icons.destination,
									parentId: 'residue-config',
								},
								{
									id: 'residue-config-residue-category-list',
									label: 'Categoría de Residuo',
									link: '/residue/residue-categorys',
									icon: Icons.residue2,
									parentId: 'residue-config',
								},
								{
									id: 'residue-config-residue-list',
									label: 'Residuos',
									link: '/residue/residues',
									icon: Icons.residue,
									parentId: 'residue-config',
								},
							],
						},
					],
				},
				{
					id: 'water-rights',
					label: 'Derechos de Agua',
					link: '/#',
					icon: 'icon-droplets',
					isChildItem: true,
					click: function (e) {
						e.preventDefault();
						setIsApex(!isApex);
					},
					stateVariables: isApex,
					childItems: [
						{
							id: 'water-rights-dashboard',
							label: 'Panel',
							link: '/water-rights/dashboard',
							icon: Icons.dashboard,
							parentId: 'water-rights',
						},
						{
							id: 'water-right-register',
							label: 'Registrar Derecho',
							link: '/water-right/register',
							icon: 'icon-chevron-right',
							parentId: 'water-right',
						},
						{
							id: 'water-right-list',
							label: 'Derechos de Agua Realizados',
							link: '/water-right/list',
							icon: 'icon-layout-list',
							parentId: 'water-rights',
						},
						{
							id: 'water-right-measurements',
							label: 'Actividades de Mediciones',
							link: '/water-right/measurements',
							icon: 'icon-gauge',
							parentId: 'water-rights',
						},
						{
							// id: 'water-right-config',
							label: 'Configuración',
							link: '/#',
							icon: Icons.settings,
							isChildItem: true,
							click: function (e) {
								e.preventDefault();
								setIsApex(!isApex);
							},
							stateVariables: isApex,
							childItems: [
								{
									// id: 'water-right-config-cultivation-unit',
									label: 'Unidad de Cultivo',
									link: '/config/water-right/cultivation-unit',
									icon: Icons.destination,
									parentId: 'water-right-config',
								},
								{
									// id: 'water-right-config-measurement-frequency',
									label: 'Frecuencia de Medición',
									link: '/config/water-right/measurement-frequency',
									icon: Icons.gauge,
									parentId: 'water-right-config',
								},
								{
									// id: 'water-right-config-position',
									label: 'Posición',
									link: '/config/water-right/position',
									icon: Icons.activity,
									parentId: 'water-right-config',
								},
								{
									// id: 'water-right-config-reporting-frequency',
									label: 'Frecuencia de Reportabilidad',
									link: '/config/water-right/reporting-frequency',
									icon: Icons.gauge,
									parentId: 'water-right-config',
								},
								{
									// id: 'water-right-config-state',
									label: 'Estado De Derecho de Agua',
									link: '/config/water-right/water-rights-status',
									icon: Icons.fileBadge,
									parentId: 'water-right-config',
								},
								{
									// id: 'water-right-config-type',
									label: 'Tipo de Derecho',
									link: '/config/water-right/type',
									icon: Icons.tags,
									parentId: 'water-right-config',
								},
								{
									// id: 'water-right-config-water-use-type',
									label: 'Uso del Agua',
									link: '/config/water-right/water-use-type',
									icon: Icons.tags,
									parentId: 'water-right-config',
								},
								{
									// id: 'config-water-right-config-use-status',
									label: 'Estado de Uso',
									link: '/config/water-right/use-status',
									icon: Icons.toggleLeft,
									parentId: 'water-right-config',
								},
								{
									// id: 'config-water-right-config-installation-location',
									label: 'Ubicación Para Instalación',
									link: '/config/water-right/installation-location',
									icon: Icons.mapPin,
									parentId: 'water-right-config',
								},
								{
									// id: 'config-water-right-config-source',
									label: 'Fuente',
									link: '/config/water-right/water-source',
									icon: Icons.water,
									parentId: 'water-right-config',
								},
								{
									// id: 'config-water-right-config-water-source-type',
									label: 'Clasificación de la Naturaleza del Agua',
									link: '/config/water-right/water-source-type',
									icon: Icons.tags,
									parentId: 'water-right-config',
								},
								{
									// id: 'config-water-right-config-activity-status',
									label: 'Estado de Actividad',
									link: '/config/water-right/activity-status',
									icon: Icons.toggleRight,
									parentId: 'water-right-config',
								},
								{
									// id: 'config-water-right-config-work-order-status',
									label: 'Estado de Obra',
									link: '/config/water-right/work-order-status',
									icon: Icons.toggleRight,
									parentId: 'water-right-config',
								},
								{
									// id: 'config-water-right-config-exercise',
									label: 'Ejercicio de Derecho',
									link: '/config/water-right/right-exercise-type',
									icon: Icons.zap,
									parentId: 'water-right-config',
								},
								{
									// id: 'config-water-right-config-flow-rate-unit',
									label: 'Unidad de Medida',
									link: '/config/water-right/flow-rate-unit',
									icon: Icons.ruler,
									parentId: 'water-right-config',
								},
								{
									// id: 'config-water-right-config-geodesic-datum',
									label: 'Dato Geodésico',
									link: '/config/water-right/geodetic-datum',
									icon: Icons.compass,
									parentId: 'water-right-config',
								},
								{
									// id: 'config-water-right-config-utm-zone',
									label: 'UTM Zona',
									link: '/config/water-right/utm-zone',
									icon: Icons.map,
									parentId: 'water-right-config',
								},
								{
									// id: 'config-water-right-config-real-estate-registry',
									label: 'Conservador de Bienes Raíces',
									link: '/config/water-right/real-estate-registry',
									icon: Icons.landmark,
									parentId: 'water-right-config',
								},
								{
									// id: 'config-water-right-config-reporting-status',
									label: 'Condición de Reportabilidad',
									link: '/config/water-right/reporting-status',
									icon: Icons.activity,
									parentId: 'water-right-config',
								},
								{
									// id: 'config-water-right-config-reporting-type',
									label: 'Tipo de Reportabilidad',
									link: '/config/water-right/reporting-type',
									icon: Icons.tags,
									parentId: 'water-right-config',
								},
								{
									// id: 'config-water-right-config-procedure-type',
									label: 'Tipo de tramite',
									link: '/config/water-right/procedure-type',
									icon: Icons.fileText,
									parentId: 'water-right-config',
								},
								{
									// id: 'config-water-right-config-document-type',
									label: 'Tipo de Documento',
									link: '/config/water-right/document-type',
									icon: Icons.document,
									parentId: 'water-right-config',
								},
								{
									// id: 'config-water-right-config-distance-unit',
									label: 'Unidad de Distancia',
									link: '/config/water-right/distance-unit',
									icon: Icons.ruler2,
									parentId: 'water-right-config',
								},
							],
						},
					],
				},
				{
					id: 'beach-cleaning',
					label: 'Limpieza de Playas',
					link: '/#',
					icon: Icons.footprints,
					isChildItem: true,
					click: function (e) {
						e.preventDefault();
						setIsApex(!isApex);
					},
					stateVariables: isApex,
					childItems: [
						{
							id: 'beach-cleaning-register',
							label: 'Registrar Limpieza',
							link: '/residue/beach-cleanings/register',
							icon: Icons.chevronRight,
							parentId: 'beach-cleaning',
						},
						{
							id: 'beach-cleaning-list',
							label: 'Limpiezas Realizadas',
							link: '/residue/beach-cleanings',
							icon: Icons.list,
							parentId: 'beach-cleaning',
						},
					],
				},

				{
					id: 'variable',
					label: 'Parámetros Ambientales',
					link: '/#',
					icon: Icons.activity,
					isChildItem: true,
					click: function (e) {
						e.preventDefault();
						setIsApex(!isApex);
					},
					stateVariables: isApex,
					childItems: [
						{
							id: 'variable-dashboard',
							label: 'Panel',
							link: '/environment/variable',
							icon: Icons.dashboard,
							parentId: 'variable',
						},
						// {
						// 	id: 'variable-download-data',
						// 	label: 'Descargar Datos',
						// 	link: '/',
						// 	icon: 'ri-download-2-fill',
						// 	parentId: 'variable',
						// },
						{
							id: 'variable-register',
							label: 'Catastro',
							link: '/environment/variable/register',
							icon: Icons.list,
							parentId: 'variable',
						},
						{
							id: 'variable-measurements',
							label: 'Mediciones',
							link: '/environment/variable/measurements',
							icon: Icons.database,
							parentId: 'variable',
						},
						{
							id: 'variable-measurements-sma',
							label: 'Mediciones SMA',
							link: '/environment/variable/measurements-sma',
							icon: Icons.database,
							parentId: 'variable',
						},
						{
							id: 'variable-incidents',
							label: 'Incidentes',
							link: '/environment/variable/incidents',
							icon: Icons.database,
							parentId: 'variable',
						},
					],
				},
				{
					id: 'fan',
					label: 'FAN',
					link: '/#',
					icon: 'icon-microscope',
					isChildItem: true,
					click: function (e) {
						e.preventDefault();
						setIsApex(!isApex);
					},
					stateVariables: isApex,
					childItems: [
						{
							id: 'fan-dashboard',
							label: 'Observer',
							link: '/monitoring-fan/dashboard',
							icon: 'icon-scan-eye',
							parentId: '',
						},
						{
							id: 'fan-config',
							label: 'Configuración',
							link: '/#',
							icon: 'icon-settings',
							isChildItem: true,
							click: function (e) {
								e.preventDefault();
								setIsApex(!isApex);
							},
							stateVariables: isApex,
							childItems: [
								{
									id: 'microalgae',
									label: 'Microalgas',
									link: '/#',
									icon: 'icon-sprout',
									parentId: '',
								},
								{
									id: 'alert',
									label: 'Alertas',
									link: '/#',
									icon: 'icon-circle-alert',
									parentId: '',
								},
							],
						},
					],
				},
				{
					id: 'riles',
					label: 'RILes',
					link: '/#',
					icon: 'icon-waves',
					isChildItem: true,
					click: function (e) {
						e.preventDefault();
						setIsApex(!isApex);
					},
					stateVariables: isApex,
					childItems: [
						{
							id: 'riles-dashboard',
							label: 'Observer',
							link: '/RILes/dashboard',
							icon: 'icon-scan-eye',
							parentId: 'variable',
						},
					],
				},
				{
					id: 'emissions',
					label: 'Emisiones',
					link: '/#',
					icon: 'ri-fire-line',
					isChildItem: true,
					click: function (e) {
						e.preventDefault();
						setIsApex(!isApex);
					},
					stateVariables: isApex,
					childItems: [
						{
							id: 'emissions-reports',
							label: 'Reportes',
							link: '/#',
							icon: 'ri-bar-chart-box-line',
							isChildItem: true,
							click: function (e) {
								e.preventDefault();
								setIsApex(!isApex);
							},
							stateVariables: isApex,
							childItems: [
								{
									id: 'emissions-reports-consumos-por-fuente',
									label: 'Consumos por Fuente',
									link: '/environment/emissions/reports/consumos-por-fuente',
									icon: 'ri-file-chart-line',
									parentId: 'emissions-reports',
								},
								{
									id: 'emissions-reports-daily-consumption-by-generation',
									label: 'Consumos diarios (generación)',
									link: '/environment/emissions/reports/daily-consumption-by-generation',
									icon: 'ri-calendar-todo-line',
									parentId: 'emissions-reports',
								},
								{
									id: 'emissions-reports-supply-by-establishment',
									label: 'Abastecimiento por Establecimiento',
									link: '/environment/emissions/reports/supply-by-establishment',
									icon: 'ri-truck-line',
									parentId: 'emissions-reports',
								},
							],
						},
					],
				},
			],
		},
		{
			id: 'logistic',
			label: 'Logística',
			icon: Icons.logistic,
			link: '/#',
			click: function (e) {
				e.preventDefault();
				setIsEstablishment(!isEstablishment);
				setIscurrentState('Establishment');
				updateIconSidebar(e);
			},
			stateVariables: isEstablishment,
			subItems: [
				{
					id: 'logistic-transfer-ground-company-list',
					label: 'Empresa de Transporte',
					link: '/logistic/companies',
					icon: Icons.handIcons,
					parentId: 'logistic-transfer-ground',
				},
				{
					id: 'logistic-transfer-ground-transport-list',
					label: 'Transportes',
					link: '/logistic/transport',
					icon: Icons.transport,
					parentId: 'logistic-transfer-ground',
				},
				{
					id: 'logistic-transfer-ground-drivers-list',
					label: 'Transportistas',
					link: '/logistic/drivers',
					icon: Icons.drivers,
					parentId: 'logistic-transfer-ground',
				},
			],
		},
		{
			id: 'documents',
			label: 'Gestión Documental',
			icon: Icons.documents,
			link: '/file-manager',
			click: function (e) {
				e.preventDefault();
				setIsEstablishment(!isEstablishment);
				setIscurrentState('Establishment');
				updateIconSidebar(e);
			},
			stateVariables: isEstablishment,
		},
		// {
		// 	id: 'screens',
		// 	label: 'Pantallas',
		// 	icon: 'ri-tv-2-line',
		// 	link: '/#',
		// 	click: function (e) {
		// 		e.preventDefault();
		// 		setIsEstablishment(!isEstablishment);
		// 		setIscurrentState('Establishment');
		// 		updateIconSidebar(e);
		// 	},
		// 	stateVariables: isEstablishment,
		// 	subItems: [
		// 		{
		// 			id: 'screens-list',
		// 			label: 'Pantallas',
		// 			link: '/screen/list',
		// 			icon: Icons.list,
		// 			parentId: 'screens',
		// 		},
		// 		{
		// 			id: 'fields-list',
		// 			label: 'Campos',
		// 			link: '/screen/fields',
		// 			icon: Icons.list,
		// 			parentId: 'screens',
		// 		},
		// 	],
		// },
		// {
		// 	id: 'residue',
		// 	label: 'Residuos',
		// 	icon: Icons.recycle,
		// 	link: 'residue/dashboard',
		// 	columns: true,
		// 	stateVariables: isEstablishment,
		// 	click: function (e) {
		// 		e.preventDefault();
		// 		setIsEstablishment(!isEstablishment);
		// 		setIscurrentState('Residue');
		// 		updateIconSidebar(e);
		// 	},
		// 	subItems: [
		// 		{
		// 			column: 1,
		// 			items: [
		// 				{ id: 'residueDashboard', label: 'Dashboard', link: '/residue/dashboard', parentId: 'residue', },
		// 			],
		// 		},
		// 		{
		// 			subtitle: 'Gestión',
		// 			column: 1,
		// 			items: [
		// 				{ id: 'guides', label: 'Guías', link: '/residue/guides', parentId: 'residue', },
		// 				{ id: 'guide-details', label: 'Gestión de Residuos', link: '/residue/guide-details', parentId: 'residue', },
		// 				{ id: 'guides-register', label: 'Registrar Guía', link: '/residue/guides/register', parentId: 'residue', },
		// 				{ id: 'warehouse-reception', label: 'Recepción Bodega', link: '/residue/warehouse/reception', parentId: 'residue', },
		// 				{ id: 'warehouse-send', label: 'Envío Bodega', link: '/residue/warehouse/send', parentId: 'residue', }
		// 			],
		// 		},
		// 		{
		// 			subtitle: 'Destino Final',
		// 			column: 2,
		// 			items: [
		// 				{ id: 'receptionFinalDestination', label: 'Recepcionar Guías', link: '/residue/final_destination/reception', parentId: 'residue', },
		// 				{ id: 'receptionFinalDestination', label: 'Prevalidar/Certificar Guías', link: '/residue/final_destination/certification-process', parentId: 'residue', }
		// 			],
		// 		},
		// 		{
		// 			subtitle: 'Mantenedores',
		// 			column: 3,
		// 			items: [
		// 				{ id: 'finalDestinations', label: 'Destinos Finales', link: '/residue/final-destinations', parentId: 'residue', },
		// 				{ id: 'residueCategories', label: 'Tipos de Residuo', link: '/residue/residue-categorys', parentId: 'residue', },
		// 				{ id: 'residues', label: 'Residuos', link: '/residue/residues', parentId: 'residue', },
		// 			],
		// 		},
		// 		{ id: 'warehouse-reception', label: 'Recepcion Bodega', link: '/residue/warehouse/reception', parentId: 'residue', },
		// 		{ id: 'warehouse-send', label: 'Envío Bodega', link: '/residue/warehouse/send', parentId: 'residue', },
		// 	],
		// },
	];

	return currentMenuItems;
};
export default Navdata;
